import {
  buildForkManifest,
  buildTimelineIndex,
  type CheckpointState,
  compareBranches,
  type BranchCompare,
  searchTimeline,
  stateAt,
  type TimelineIndex,
} from '@vibetime/timetravel';
import type { LedgerEntry } from '@vibetime/runtime';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * /timetravel — Canvas-backed timeline scrubber for the live dashboard.
 *
 * Built to handle 10k+ entries: one <canvas> draw per interaction, no
 * per-entry DOM nodes along the spine. Keyboard shuttle (J/K/L) is
 * intentional — every editor-native dev flinches toward it.
 */

const KIND_COLORS: Record<string, string> = {
  run_start: '#28d7ff',
  run_end: '#28d7ff',
  plan: '#88a4ff',
  agent_start: '#53f6c4',
  agent_end: '#53f6c4',
  tool_call: '#ffcb6b',
  tool_result: '#ffcb6b',
  diff_proposed: '#ff6e7a',
  diff_applied: '#ff6e7a',
  test_result: '#28d7ff',
  eval_result: '#28d7ff',
  critic_review: '#88a4ff',
  approval_request: '#ffcb6b',
  approval_decision: '#ffcb6b',
  citation: '#9ab3c6',
  cost: '#9ab3c6',
  error: '#ff7588',
};

type ModelOption = { id: string; label: string };
const MODEL_OPTIONS: ModelOption[] = [
  { id: 'claude-opus-4-7', label: 'Claude Opus 4.7' },
  { id: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6' },
  { id: 'claude-haiku-4-5', label: 'Claude Haiku 4.5' },
  { id: 'gpt-5.3', label: 'GPT-5.3' },
  { id: 'gemini-3.1', label: 'Gemini 3.1' },
];

function useRunEntries(runId: string | null): LedgerEntry[] {
  const [entries, setEntries] = useState<LedgerEntry[]>([]);
  useEffect(() => {
    if (!runId) return;
    let cancelled = false;
    fetch(`/api/run/${runId}`)
      .then((r) => r.json() as Promise<{ entries: LedgerEntry[] }>)
      .then((data) => {
        if (!cancelled) setEntries(data.entries ?? []);
      })
      .catch(() => {
        if (!cancelled) setEntries([]);
      });
    return () => {
      cancelled = true;
    };
  }, [runId]);
  return entries;
}

function useRunList(): string[] {
  const [runs, setRuns] = useState<string[]>([]);
  useEffect(() => {
    let cancelled = false;
    fetch('/api/runs')
      .then((r) => r.json() as Promise<{ runs?: Array<{ runId: string }> }>)
      .then((data) => {
        if (cancelled) return;
        setRuns((data.runs ?? []).map((r) => r.runId));
      })
      .catch(() => {
        if (!cancelled) setRuns([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);
  return runs;
}

export function TimetravelView(): JSX.Element {
  const runs = useRunList();
  const initialRun = useMemo(() => readQueryParam('run'), []);
  const [runA, setRunA] = useState<string | null>(initialRun ?? null);
  const [runB, setRunB] = useState<string | null>(readQueryParam('b'));
  const entriesA = useRunEntries(runA);
  const entriesB = useRunEntries(runB);

  const indexA = useMemo(() => buildTimelineIndex(entriesA), [entriesA]);
  const indexB = useMemo(() => (runB ? buildTimelineIndex(entriesB) : null), [entriesB, runB]);

  const [selected, setSelected] = useState<number>(-1);
  const [search, setSearch] = useState('');
  const [playing, setPlaying] = useState(false);
  const [forkModel, setForkModel] = useState<string>('');
  const [forkPrompt, setForkPrompt] = useState('');
  const [forkPreview, setForkPreview] = useState<string | null>(null);

  useEffect(() => {
    if (indexA.nodes.length && selected < 0) setSelected(indexA.nodes.length - 1);
    if (selected >= indexA.nodes.length) setSelected(indexA.nodes.length - 1);
  }, [indexA.nodes.length, selected]);

  const filtered = useMemo(() => {
    if (!search) return null;
    const matches = searchTimeline(indexA, { text: search });
    return new Set(matches.map((n) => n.index));
  }, [search, indexA]);

  useKeyboardShuttle({
    onPrev: () => setSelected((s) => Math.max(0, s - 1)),
    onNext: () => setSelected((s) => Math.min(indexA.nodes.length - 1, s + 1)),
    onToggle: () => setPlaying((p) => !p),
  });

  useEffect(() => {
    if (!playing) return;
    const handle = window.setInterval(() => {
      setSelected((s) => {
        if (s >= indexA.nodes.length - 1) {
          setPlaying(false);
          return s;
        }
        return s + 1;
      });
    }, 200);
    return () => window.clearInterval(handle);
  }, [playing, indexA.nodes.length]);

  const checkpointId = indexA.nodes[selected]?.entry.id ?? null;
  const snapshot: CheckpointState | null = useMemo(
    () => (checkpointId ? stateAt(indexA, checkpointId) : null),
    [indexA, checkpointId],
  );

  const compare: BranchCompare | null = useMemo(
    () => (indexB ? compareBranches(entriesA, entriesB) : null),
    [indexB, entriesA, entriesB],
  );

  const handleFork = useCallback(() => {
    if (!indexA || !checkpointId || !runA) return;
    const manifest = buildForkManifest(indexA, {
      parentRunId: runA,
      parentEntryId: checkpointId,
      forkRunId: `${runA}-fork-${Date.now().toString(36)}`,
      modelOverride: forkModel || undefined,
      nextPrompt: forkPrompt || undefined,
    });
    setForkPreview(JSON.stringify(manifest, null, 2));
  }, [indexA, checkpointId, runA, forkModel, forkPrompt]);

  return (
    <div className="tt-root">
      <header className="tt-topbar">
        <div>
          <div className="tt-eyebrow">Timetravel</div>
          <h1>Scrub the chain. Fork the outcome.</h1>
        </div>
        <div className="tt-runpick">
          <label>
            <span>run A</span>
            <select value={runA ?? ''} onChange={(e) => setRunA(e.target.value || null)}>
              <option value="">— pick a run —</option>
              {runs.map((id) => (
                <option key={id} value={id}>
                  {id}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>run B (compare)</span>
            <select value={runB ?? ''} onChange={(e) => setRunB(e.target.value || null)}>
              <option value="">— none —</option>
              {runs.map((id) => (
                <option key={id} value={id}>
                  {id}
                </option>
              ))}
            </select>
          </label>
        </div>
      </header>

      <section className="tt-scrubber-card">
        <TimelineCanvas
          index={indexA}
          selectedIdx={selected}
          filtered={filtered}
          onSelect={setSelected}
        />
        <div className="tt-shuttle">
          <button type="button" onClick={() => setSelected((s) => Math.max(0, s - 1))}>
            ◀ J
          </button>
          <button type="button" onClick={() => setPlaying((p) => !p)}>
            {playing ? 'pause · K' : 'play · K'}
          </button>
          <button
            type="button"
            onClick={() => setSelected((s) => Math.min(indexA.nodes.length - 1, s + 1))}
          >
            L ▶
          </button>
          <input
            className="tt-search-input"
            placeholder="/ search kind, tool, payload…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <span className="tt-hint">
            {selected + 1}/{indexA.nodes.length} entries
          </span>
        </div>
      </section>

      <section className="tt-grid">
        <div className="tt-pane">
          <h2>Checkpoint</h2>
          {snapshot ? (
            <dl className="tt-kv">
              <dt>entry</dt>
              <dd>
                #{snapshot.entryIndex + 1} / {indexA.nodes.length}
              </dd>
              <dt>kind</dt>
              <dd>{indexA.nodes[selected]?.entry.kind ?? '—'}</dd>
              <dt>model</dt>
              <dd>{snapshot.currentModelId ?? '—'}</dd>
              <dt>cost</dt>
              <dd>${snapshot.costUsd.toFixed(4)}</dd>
              <dt>goal</dt>
              <dd>{snapshot.goal ?? '—'}</dd>
            </dl>
          ) : (
            <p className="tt-muted">pick a run on the right</p>
          )}
        </div>

        <div className="tt-pane">
          <h2>Plan tree</h2>
          <ul className="tt-list">
            {(snapshot?.planTasks ?? []).map((t) => (
              <li key={t.id}>
                <strong>{t.id}</strong> · {t.role}
                <span className={`tt-status tt-status-${t.status}`}>{t.status}</span>
              </li>
            ))}
            {!snapshot?.planTasks.length && <li className="tt-muted">no plan yet</li>}
          </ul>
        </div>

        <div className="tt-pane">
          <h2>Open tool calls</h2>
          <ul className="tt-list">
            {(snapshot?.openToolCalls ?? []).map((c) => (
              <li key={c.callId}>
                <strong>{c.name}</strong>
                {c.argsSummary ? <span className="tt-muted"> · {c.argsSummary}</span> : null}
              </li>
            ))}
            {!snapshot?.openToolCalls.length && <li className="tt-muted">no open tool calls</li>}
          </ul>
        </div>

        <div className="tt-pane">
          <h2>File snapshots</h2>
          <ul className="tt-list">
            {(snapshot?.fileSnapshots ?? []).map((f) => (
              <li key={f.path}>
                <strong>{f.path}</strong>
                <span className="tt-muted"> @ {f.blobSha.slice(0, 8)}</span>
              </li>
            ))}
            {!snapshot?.fileSnapshots.length && <li className="tt-muted">no snapshots yet</li>}
          </ul>
        </div>
      </section>

      <section className="tt-pane tt-fork">
        <h2>Fork from here</h2>
        <div className="tt-fork-controls">
          <label>
            <span>swap model</span>
            <select value={forkModel} onChange={(e) => setForkModel(e.target.value)}>
              <option value="">keep current</option>
              {MODEL_OPTIONS.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.label}
                </option>
              ))}
            </select>
          </label>
          <label className="tt-prompt-label">
            <span>edit next message</span>
            <textarea
              value={forkPrompt}
              onChange={(e) => setForkPrompt(e.target.value)}
              rows={3}
              placeholder="leave blank to keep the original next step"
            />
          </label>
          <button className="tt-fork-button" type="button" onClick={handleFork} disabled={!checkpointId}>
            Fork at #{selected + 1}
          </button>
        </div>
        {forkPreview ? <pre className="tt-fork-preview">{forkPreview}</pre> : null}
      </section>

      {compare ? (
        <section className="tt-pane tt-compare">
          <h2>Branch comparison · {compare.a.runId} ↔ {compare.b.runId}</h2>
          <p className="tt-muted">
            {compare.pairs.length} steps · shared prefix {compare.sharedPrefixLength}
          </p>
          <div className="tt-compare-grid">
            <div>
              <h3>{compare.a.runId}</h3>
              <FinalStateTiles state={compare.final.a} />
            </div>
            <div>
              <h3>{compare.b.runId}</h3>
              <FinalStateTiles state={compare.final.b} />
            </div>
          </div>
        </section>
      ) : null}

      <footer className="tt-footer">
        <kbd>J</kbd> prev · <kbd>K</kbd> play/pause · <kbd>L</kbd> next · <kbd>/</kbd> search
      </footer>
      <style>{TIMETRAVEL_CSS}</style>
    </div>
  );
}

function FinalStateTiles({ state }: { state: CheckpointState | null }): JSX.Element {
  if (!state) return <p className="tt-muted">no run</p>;
  return (
    <dl className="tt-kv">
      <dt>entries</dt>
      <dd>{state.entryIndex + 1}</dd>
      <dt>cost</dt>
      <dd>${state.costUsd.toFixed(4)}</dd>
      <dt>model</dt>
      <dd>{state.currentModelId ?? '—'}</dd>
      <dt>tasks</dt>
      <dd>{state.planTasks.length}</dd>
    </dl>
  );
}

interface CanvasProps {
  index: TimelineIndex;
  selectedIdx: number;
  filtered: Set<number> | null;
  onSelect: (i: number) => void;
}

function TimelineCanvas({ index, selectedIdx, filtered, onSelect }: CanvasProps): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [viewStart, setViewStart] = useState(0);
  const [viewEnd, setViewEnd] = useState(1);
  const dragRef = useRef<{ startX: number; startViewStart: number; startViewEnd: number } | null>(null);

  useEffect(() => {
    setViewStart(0);
    setViewEnd(1);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const w = canvas.clientWidth * dpr;
    const h = canvas.clientHeight * dpr;
    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w;
      canvas.height = h;
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const cw = canvas.clientWidth;
    const ch = canvas.clientHeight;
    ctx.clearRect(0, 0, cw, ch);
    ctx.fillStyle = '#050d15';
    ctx.fillRect(0, 0, cw, ch);

    if (index.nodes.length === 0) return;
    const range = Math.max(1, index.endMs - index.startMs);
    const viewRange = Math.max(0.0001, viewEnd - viewStart);

    for (let i = 0; i < index.nodes.length; i++) {
      const node = index.nodes[i];
      if (!node) continue;
      if (filtered && !filtered.has(i)) continue;
      const frac = (node.entry.timestamp - index.startMs) / range;
      if (frac < viewStart || frac > viewEnd) continue;
      const x = ((frac - viewStart) / viewRange) * (cw - 12) + 6;
      ctx.fillStyle = KIND_COLORS[node.entry.kind] ?? '#e7f2fb';
      ctx.globalAlpha = i === selectedIdx ? 1 : 0.7;
      ctx.fillRect(x - 1, 14, 2, ch - 28);
    }
    ctx.globalAlpha = 1;

    const selected = index.nodes[selectedIdx];
    if (selected) {
      const sFrac = (selected.entry.timestamp - index.startMs) / range;
      if (sFrac >= viewStart && sFrac <= viewEnd) {
        const sx = ((sFrac - viewStart) / viewRange) * (cw - 12) + 6;
        ctx.strokeStyle = '#e7f2fb';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(Math.round(sx) + 0.5, 0);
        ctx.lineTo(Math.round(sx) + 0.5, ch);
        ctx.stroke();
      }
    }
  }, [index, selectedIdx, filtered, viewStart, viewEnd]);

  const handleWheel = useCallback((event: React.WheelEvent<HTMLCanvasElement>) => {
    event.preventDefault();
    const zoomFactor = event.deltaY < 0 ? 0.85 : 1 / 0.85;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const px = (event.clientX - rect.left) / rect.width;
    setViewStart((s) => {
      setViewEnd((e) => {
        const viewRange = e - s;
        const anchor = s + viewRange * px;
        const newRange = Math.max(0.001, Math.min(1, viewRange * zoomFactor));
        let newStart = anchor - newRange * px;
        let newEnd = newStart + newRange;
        if (newStart < 0) { newEnd -= newStart; newStart = 0; }
        if (newEnd > 1) { newStart -= newEnd - 1; newEnd = 1; }
        return Math.min(1, Math.max(newStart + 0.001, newEnd));
      });
      return Math.max(0, Math.min(1 - 0.001, s));
    });
  }, []);

  const handleMouseDown = useCallback(
    (event: React.MouseEvent<HTMLCanvasElement>) => {
      if (!event.shiftKey) return;
      dragRef.current = { startX: event.clientX, startViewStart: viewStart, startViewEnd: viewEnd };
    },
    [viewStart, viewEnd],
  );

  const handleMouseMove = useCallback((event: React.MouseEvent<HTMLCanvasElement>) => {
    const drag = dragRef.current;
    if (!drag) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const deltaFrac = ((event.clientX - drag.startX) / rect.width) * (drag.startViewEnd - drag.startViewStart);
    let newStart = drag.startViewStart - deltaFrac;
    let newEnd = drag.startViewEnd - deltaFrac;
    if (newStart < 0) { newEnd -= newStart; newStart = 0; }
    if (newEnd > 1) { newStart -= newEnd - 1; newEnd = 1; }
    setViewStart(newStart);
    setViewEnd(newEnd);
  }, []);

  const handleMouseUp = useCallback(() => {
    dragRef.current = null;
  }, []);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current;
      if (!canvas || index.nodes.length === 0) return;
      if (event.shiftKey) return;
      const rect = canvas.getBoundingClientRect();
      const px = event.clientX - rect.left;
      const range = Math.max(1, index.endMs - index.startMs);
      const viewRange = Math.max(0.0001, viewEnd - viewStart);
      let best = 0;
      let bestDx = Infinity;
      for (let i = 0; i < index.nodes.length; i++) {
        const node = index.nodes[i];
        if (!node) continue;
        const frac = (node.entry.timestamp - index.startMs) / range;
        const x = ((frac - viewStart) / viewRange) * (rect.width - 12) + 6;
        const dx = Math.abs(x - px);
        if (dx < bestDx) {
          best = i;
          bestDx = dx;
        }
      }
      onSelect(best);
    },
    [index, onSelect, viewStart, viewEnd],
  );

  return (
    <canvas
      ref={canvasRef}
      className="tt-canvas"
      onClick={handleClick}
      onWheel={handleWheel}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      title="click to scrub · wheel to zoom · shift-drag to pan"
    />
  );
}

interface ShuttleHandlers {
  onPrev: () => void;
  onNext: () => void;
  onToggle: () => void;
}

function useKeyboardShuttle(handlers: ShuttleHandlers): void {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return;
      const key = event.key.toLowerCase();
      if (key === 'j') {
        event.preventDefault();
        handlers.onPrev();
      } else if (key === 'l') {
        event.preventDefault();
        handlers.onNext();
      } else if (key === 'k') {
        event.preventDefault();
        handlers.onToggle();
      } else if (event.key === '/') {
        const input = document.querySelector<HTMLInputElement>('.tt-search-input');
        if (input) {
          event.preventDefault();
          input.focus();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handlers]);
}

function readQueryParam(key: string): string | null {
  if (typeof window === 'undefined') return null;
  return new URLSearchParams(window.location.search).get(key);
}

const TIMETRAVEL_CSS = `
.tt-root { min-height: 100vh; background: #05090f; color: #e7f2fb;
  font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
  padding: 28px; display: grid; gap: 20px; max-width: 1320px; margin: 0 auto; }
.tt-topbar { display: grid; grid-template-columns: 1fr auto; gap: 24px; align-items: end; }
.tt-eyebrow { font-size: 12px; color: #28d7ff; letter-spacing: 0.16em; text-transform: uppercase; }
.tt-topbar h1 { font-size: 28px; margin: 4px 0 0; font-weight: 600; }
.tt-runpick { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.tt-runpick label { display: grid; gap: 4px; font-size: 12px; color: #9ab3c6; }
.tt-runpick select { background: #0d1823; color: #e7f2fb; border: 1px solid rgba(151,188,211,0.28);
  border-radius: 8px; padding: 8px 10px; font-family: inherit; min-width: 240px; }
.tt-scrubber-card { background: rgba(12,21,31,0.82); border: 1px solid rgba(151,188,211,0.16);
  border-radius: 22px; padding: 18px; }
.tt-canvas { width: 100%; height: 140px; display: block; border-radius: 14px; background: #050d15; cursor: crosshair; }
.tt-shuttle { margin-top: 14px; display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
.tt-shuttle button { background: #0d1823; color: #e7f2fb; border: 1px solid rgba(151,188,211,0.28);
  border-radius: 8px; padding: 8px 14px; font-family: inherit; cursor: pointer; }
.tt-shuttle button:hover { border-color: #28d7ff; }
.tt-search-input { flex: 1; min-width: 220px; background: #0d1823; color: #e7f2fb;
  border: 1px solid rgba(151,188,211,0.28); border-radius: 8px; padding: 8px 10px; font-family: inherit; }
.tt-hint { color: #9ab3c6; font-size: 12px; margin-left: auto; }
.tt-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 18px; }
.tt-pane { background: rgba(12,21,31,0.82); border: 1px solid rgba(151,188,211,0.16);
  border-radius: 18px; padding: 18px; }
.tt-pane h2 { margin: 0 0 10px; font-size: 12px; color: #9ab3c6; text-transform: uppercase; letter-spacing: 0.12em; }
.tt-pane h3 { margin: 0 0 6px; font-size: 14px; color: #e7f2fb; }
.tt-list { list-style: none; margin: 0; padding: 0; }
.tt-list li { padding: 6px 0; border-bottom: 1px dashed rgba(151,188,211,0.16); font-size: 13px; }
.tt-list li:last-child { border-bottom: 0; }
.tt-kv { display: grid; grid-template-columns: auto 1fr; gap: 4px 14px; font-size: 13px; }
.tt-kv dt { color: #9ab3c6; }
.tt-status { margin-left: 8px; font-size: 11px; color: #9ab3c6; }
.tt-status-running { color: #ffcb6b; }
.tt-status-completed { color: #53f6c4; }
.tt-status-failed { color: #ff7588; }
.tt-muted { color: #6d8698; font-size: 13px; }
.tt-fork-controls { display: grid; grid-template-columns: auto 1fr auto; gap: 12px; align-items: end; }
.tt-fork-controls label { display: grid; gap: 4px; font-size: 12px; color: #9ab3c6; }
.tt-fork-controls select, .tt-fork-controls textarea {
  background: #0d1823; color: #e7f2fb; border: 1px solid rgba(151,188,211,0.28);
  border-radius: 8px; padding: 8px 10px; font-family: inherit; }
.tt-prompt-label textarea { width: 100%; resize: vertical; }
.tt-fork-button { background: #28d7ff; color: #05090f; border: 0; border-radius: 8px;
  padding: 10px 16px; font-weight: 600; cursor: pointer; font-family: inherit; }
.tt-fork-button:disabled { opacity: 0.5; cursor: not-allowed; }
.tt-fork-preview { margin-top: 14px; padding: 12px; background: #050d15; border-radius: 10px;
  overflow: auto; font-family: 'JetBrains Mono', ui-monospace, Menlo, monospace; font-size: 12px; max-height: 240px; }
.tt-compare-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.tt-footer { color: #6d8698; font-size: 12px; }
.tt-footer kbd { background: rgba(12,21,31,0.82); border: 1px solid rgba(151,188,211,0.28);
  padding: 2px 6px; border-radius: 4px; font-family: inherit; }
`;
